import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Modal from '../../components/Modal';
import BopRowActions, { RowIcon } from '../../components/BopRowActions';
import AccessoNegato from '../../components/permessi/AccessoNegato';
import { usePermesso } from '../../context/PermessiContext';
import { apiFetch } from '../../lib/api';
import '../../styles/impostazioni.css';

// ── Tipi ──────────────────────────────────────────────────────────────────────

interface Profilo {
  id:           number;
  nome:         string;
  descrizione:  string | null;
  attivo:       boolean;
  vociAttive:   number;
  vociTotali:   number;
  assegnazioni: number;
}

const FORM_VUOTO = { nome: '', descrizione: '', copiaDaProfiloId: '' };

// ── Componente ────────────────────────────────────────────────────────────────

export default function ProfiliPage() {
  const { isSuperadmin } = usePermesso();
  const navigate = useNavigate();

  const [profili, setProfili] = useState<Profilo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [msg,     setMsg]     = useState('');

  const [modaleAperta, setModaleAperta] = useState(false);
  const [form,         setForm]         = useState(FORM_VUOTO);
  const [salvando,     setSalvando]     = useState(false);

  const carica = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // @api GET /api/profili -> {"success":"true","data":"Profilo[]"}
      const d = await apiFetch<{ data: Profilo[] }>('/api/profili');
      setProfili(d.data);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!isSuperadmin) return;
    carica();
  }, [isSuperadmin, carica]);

  const apriNuovo = (copiaDa?: Profilo) => {
    setForm(copiaDa
      ? { nome: `${copiaDa.nome} (copia)`, descrizione: copiaDa.descrizione ?? '', copiaDaProfiloId: String(copiaDa.id) }
      : FORM_VUOTO);
    setMsg('');
    setError('');
    setModaleAperta(true);
  };

  const salva = async () => {
    if (!form.nome.trim()) { setError('Il nome del profilo è obbligatorio'); return; }
    setSalvando(true);
    setError('');
    try {
      // @api POST /api/profili {"nome":"string","descrizione":"string","copiaDaProfiloId":"number"} -> {"success":"true","data":"{id:number}"}
      const d = await apiFetch<{ data: { id: number } }>('/api/profili', {
        method: 'POST',
        body: JSON.stringify({
          nome:             form.nome.trim(),
          descrizione:      form.descrizione.trim() || null,
          copiaDaProfiloId: form.copiaDaProfiloId ? Number(form.copiaDaProfiloId) : undefined,
        }),
      });
      setModaleAperta(false);
      navigate(`/utenti/profili/${d.data.id}`);
    } catch (e: any) { setError(e.message); }
    finally { setSalvando(false); }
  };

  const elimina = async (p: Profilo) => {
    if (!window.confirm(`Eliminare il profilo "${p.nome}"?`)) return;
    setError('');
    setMsg('');
    try {
      // @api DELETE /api/profili/:id -> {"success":"true"}
      await apiFetch(`/api/profili/${p.id}`, { method: 'DELETE' });
      setMsg(`Profilo "${p.nome}" eliminato.`);
      carica();
    } catch (e: any) { setError(e.message); }
  };

  if (!isSuperadmin) {
    return (
      <Layout>
        <AccessoNegato messaggio="Solo un Superadmin può gestire i profili permessi." />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h2>Profili permessi</h2>
          <p>
            Un profilo è un set di permessi riutilizzabile. Gli utenti che lo usano lo
            ereditano in tempo reale: modifichi il profilo una volta e la modifica arriva
            a tutti, tranne dove hai impostato una deroga individuale.
          </p>
        </div>
        <button className="bop-btn bop-btn-primary" onClick={() => apriNuovo()}>
          + Nuovo profilo
        </button>
      </div>

      {error && <div className="bop-inline-error">{error}</div>}
      {msg   && <div className="bop-inline-success">{msg}</div>}

      <div className="bop-card">
        <table className="bop-table bop-table--clickable">
          <thead>
            <tr>
              <th>Profilo</th>
              <th>Descrizione</th>
              <th>Voci abilitate</th>
              <th className="bop-text-right">Assegnazioni</th>
              <th>Stato</th>
              <th className="bop-table__th--actions">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6}><div className="bop-empty-state">Caricamento…</div></td></tr>
            ) : profili.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <div className="bop-empty-state">
                    Nessun profilo definito. Creane uno per smettere di compilare i permessi utente per utente.
                  </div>
                </td>
              </tr>
            ) : profili.map(p => (
              <tr key={p.id} onClick={() => navigate(`/utenti/profili/${p.id}`)}>
                <td>{p.nome}</td>
                <td className="bop-text-muted">{p.descrizione || '—'}</td>
                <td>
                  <div className="impostazioni-profili-copertura">
                    <div className="impostazioni-profili-barra">
                      <div
                        className="impostazioni-profili-barra-fill"
                        /* Larghezza calcolata a runtime dalla copertura del profilo */
                        style={{ width: `${Math.round((p.vociAttive / Math.max(p.vociTotali, 1)) * 100)}%` }}
                      />
                    </div>
                    <span className="impostazioni-profili-copertura-testo">
                      {p.vociAttive}/{p.vociTotali}
                    </span>
                  </div>
                </td>
                <td className="bop-text-right">{p.assegnazioni}</td>
                <td>
                  <span className={`bop-badge ${p.attivo ? 'bop-badge-success' : 'bop-badge-neutral'}`}>
                    {p.attivo ? 'Attivo' : 'Non attivo'}
                  </span>
                </td>
                <td className="bop-table__td--actions">
                  <BopRowActions actions={[
                    { icon: <RowIcon.Copy />,  label: 'Duplica', onClick: () => apriNuovo(p) },
                    { icon: <RowIcon.Trash />, label: 'Elimina', onClick: () => elimina(p),
                      variant: 'danger', hidden: p.assegnazioni > 0 },
                  ]} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modaleAperta && (
        <Modal
          title="Nuovo profilo"
          onClose={() => setModaleAperta(false)}
          footer={
            <>
              <button className="bop-btn bop-btn-secondary" onClick={() => setModaleAperta(false)}>
                Annulla
              </button>
              <button className="bop-btn bop-btn-primary" onClick={salva} disabled={salvando}>
                {salvando ? 'Creo…' : 'Crea profilo'}
              </button>
            </>
          }
        >
          <div className="bop-form-group">
            <label className="bop-label">Nome *</label>
            <input
              className="bop-input"
              value={form.nome}
              onChange={e => setForm({ ...form, nome: e.target.value })}
              placeholder="es. Contabile"
              autoFocus
            />
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Descrizione</label>
            <input
              className="bop-input"
              value={form.descrizione}
              onChange={e => setForm({ ...form, descrizione: e.target.value })}
              placeholder="A cosa serve questo profilo"
            />
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Copia permessi da</label>
            <select
              className="bop-select"
              value={form.copiaDaProfiloId}
              onChange={e => setForm({ ...form, copiaDaProfiloId: e.target.value })}
            >
              <option value="">— Parti da zero —</option>
              {profili.map(p => (
                <option key={p.id} value={p.id}>{p.nome}</option>
              ))}
            </select>
          </div>
        </Modal>
      )}
    </Layout>
  );
}
